const CHAT_UNREAD_COUNTS_KEY = 'adminChatUnreadCounts';
const CHAT_NOTIFICATIONS_KEY = 'adminChatNotifications';
const CHAT_ACTIVE_CONTACT_KEY = 'adminChatActiveContact';
const CHAT_ACTIVITY_MAP_KEY = 'adminChatActivityMap';
const CHAT_ACTIVITY_MAP_VERSION_KEY = 'adminChatActivityMapVersion';
const CHAT_ACTIVITY_MAP_VERSION = '2';
const ANNOUNCEMENT_UNREAD_KEY = 'adminAnnouncementUnreadCount';
const MAX_NOTIFICATIONS = 30;

function parseJson(value, fallback) {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function toTimestamp(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = new Date(value || '').getTime();
  return Number.isFinite(parsed) ? parsed : Date.now();
}

export function getUnreadCounts() {
  return parseJson(localStorage.getItem(CHAT_UNREAD_COUNTS_KEY), {});
}

export function getUnreadTotal() {
  const counts = getUnreadCounts();
  return Object.values(counts).reduce((acc, value) => acc + Number(value || 0), 0);
}

export function getAnnouncementUnreadTotal() {
  return Number(localStorage.getItem(ANNOUNCEMENT_UNREAD_KEY) || 0);
}

export function getCombinedUnreadTotal() {
  return getUnreadTotal() + getAnnouncementUnreadTotal();
}

export function getNotifications() {
  return parseJson(localStorage.getItem(CHAT_NOTIFICATIONS_KEY), []);
}

export function getChatActivityMap() {
  const currentVersion = localStorage.getItem(CHAT_ACTIVITY_MAP_VERSION_KEY);
  if (currentVersion !== CHAT_ACTIVITY_MAP_VERSION) {
    localStorage.setItem(CHAT_ACTIVITY_MAP_KEY, JSON.stringify({}));
    localStorage.setItem(CHAT_ACTIVITY_MAP_VERSION_KEY, CHAT_ACTIVITY_MAP_VERSION);
    return {};
  }
  return parseJson(localStorage.getItem(CHAT_ACTIVITY_MAP_KEY), {});
}

export function setChatActivity(contactId, timestamp = Date.now()) {
  if (!contactId) return;
  const activityMap = getChatActivityMap();
  const incoming = toTimestamp(timestamp);
  const existing = Number(activityMap[contactId] || 0);
  activityMap[contactId] = Math.max(existing, incoming);
  localStorage.setItem(CHAT_ACTIVITY_MAP_KEY, JSON.stringify(activityMap));
  window.dispatchEvent(
    new CustomEvent('admin-chat-unread-updated', {
      detail: { type: 'activity-updated', contactId },
    }),
  );
}

export function setActiveChatContact(contactId) {
  if (!contactId) {
    localStorage.removeItem(CHAT_ACTIVE_CONTACT_KEY);
    return;
  }
  localStorage.setItem(CHAT_ACTIVE_CONTACT_KEY, String(contactId));
}

export function getActiveChatContact() {
  return localStorage.getItem(CHAT_ACTIVE_CONTACT_KEY) || '';
}

export function markContactAsRead(contactId) {
  if (!contactId) return;
  const counts = getUnreadCounts();
  if (!counts[contactId]) return;
  delete counts[contactId];
  localStorage.setItem(CHAT_UNREAD_COUNTS_KEY, JSON.stringify(counts));
  window.dispatchEvent(
    new CustomEvent('admin-chat-unread-updated', {
      detail: { type: 'mark-read', contactId },
    }),
  );
}

export function clearAllChatNotifications() {
  localStorage.setItem(CHAT_UNREAD_COUNTS_KEY, JSON.stringify({}));
  localStorage.setItem(CHAT_NOTIFICATIONS_KEY, JSON.stringify([]));
  localStorage.setItem(ANNOUNCEMENT_UNREAD_KEY, '0');
  window.dispatchEvent(
    new CustomEvent('admin-chat-unread-updated', {
      detail: { type: 'clear-all' },
    }),
  );
}

export function markAllChatMessagesAsRead() {
  localStorage.setItem(CHAT_UNREAD_COUNTS_KEY, JSON.stringify({}));
  window.dispatchEvent(
    new CustomEvent('admin-chat-unread-updated', {
      detail: { type: 'mark-all-chat-read' },
    }),
  );
}

export function removeChatNotificationsForConversation(conversationId) {
  if (!conversationId) return;
  const notifications = getNotifications();
  const next = notifications.filter((item) => item?.conversationId !== conversationId);
  if (next.length === notifications.length) return;
  localStorage.setItem(CHAT_NOTIFICATIONS_KEY, JSON.stringify(next));
  window.dispatchEvent(
    new CustomEvent('admin-chat-unread-updated', {
      detail: { type: 'conversation-opened', conversationId },
    }),
  );
}

export function pushAnnouncementNotification({
  eventId,
  title,
  message,
  senderName,
  createdAt,
}) {
  const normalizedTimestamp = toTimestamp(createdAt);
  const notifications = getNotifications();
  const eventKey = eventId ? `announcement:${String(eventId)}` : '';
  if (eventKey && notifications.some((item) => item.eventKey === eventKey)) {
    return;
  }
  const next = [
    {
      id: eventId ? `announcement-${eventId}` : `announcement-${Date.now()}`,
      eventKey,
      type: 'announcement',
      senderId: '',
      senderName: senderName || 'Announcement',
      title: title || 'Announcement',
      content: message || '',
      hasAttachment: false,
      createdAt: new Date(normalizedTimestamp).toISOString(),
      conversationId: '',
    },
    ...notifications,
  ].slice(0, MAX_NOTIFICATIONS);
  localStorage.setItem(CHAT_NOTIFICATIONS_KEY, JSON.stringify(next));
  const prev = getAnnouncementUnreadTotal();
  localStorage.setItem(ANNOUNCEMENT_UNREAD_KEY, String(prev + 1));
  window.dispatchEvent(
    new CustomEvent('admin-chat-unread-updated', {
      detail: {
        type: 'announcement',
        createdAt: new Date(normalizedTimestamp).toISOString(),
      },
    }),
  );
}

export function markAnnouncementsAsRead() {
  localStorage.setItem(ANNOUNCEMENT_UNREAD_KEY, '0');
  window.dispatchEvent(
    new CustomEvent('admin-chat-unread-updated', {
      detail: { type: 'announcement-read' },
    }),
  );
}

export function getCombinedNotifications() {
  return getNotifications();
}

export function pushChatNotification({
  eventId,
  senderId,
  senderName,
  content,
  attachmentUrls,
  createdAt,
  conversationId,
  incrementUnread = true,
}) {
  const normalizedTimestamp = toTimestamp(createdAt);
  const notifications = getNotifications();
  const eventKey = eventId ? `message:${String(eventId)}` : '';
  const isDuplicate =
    eventKey && notifications.some((item) => item.eventKey === eventKey);
  if (isDuplicate) {
    return;
  }

  if (senderId) {
    setChatActivity(senderId, normalizedTimestamp);
  }

  const next = [
    {
      id: eventId
        ? `${conversationId || 'chat'}-${eventId}`
        : `${conversationId || 'chat'}-${Date.now()}`,
      eventKey,
      type: 'chat',
      senderId: senderId || '',
      senderName: senderName || 'New message',
      content: content || '',
      hasAttachment: Array.isArray(attachmentUrls) && attachmentUrls.length > 0,
      createdAt: new Date(normalizedTimestamp).toISOString(),
      conversationId: conversationId || '',
    },
    ...notifications,
  ].slice(0, MAX_NOTIFICATIONS);
  localStorage.setItem(CHAT_NOTIFICATIONS_KEY, JSON.stringify(next));

  if (incrementUnread && senderId) {
    const counts = getUnreadCounts();
    counts[senderId] = Number(counts[senderId] || 0) + 1;
    localStorage.setItem(CHAT_UNREAD_COUNTS_KEY, JSON.stringify(counts));
  }

  window.dispatchEvent(
    new CustomEvent('admin-chat-unread-updated', {
      detail: {
        type: 'new-message',
        senderId: senderId || '',
        createdAt: new Date(normalizedTimestamp).toISOString(),
      },
    }),
  );
}
